import React, { ReactNode } from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import compteApi from '../../../../../network/api/compteApi';
import { Entity, TypeEntityEnum } from '../../../../../network/api/types/compte/Entity';
import { LocationDTO } from '../../../../../network/api/types/compte/LocationDTO';
import { HOME_PAGE_ROUTE } from '../../../../../routes';
import { getStructureName } from '../../../../../services/utils';
import ConfirmationModal from '../../../../common/modals/ConfirmationModal';
import { toastError } from '../../../../common/Notification';
import Loader from '../../../../Loader';
import StructureInformation from './StructureInformation';

type LocationState = {
  isManager: boolean;
};

type Props = RouteComponentProps<unknown, null, LocationState> & {
  onClose: () => void
  entityId: string
  setDirty?: (dirty: boolean, callback?: () => void) => void
  setModalI18n?: (title: string, subtitle: string) => void
  backButton?: ReactNode
};

type State = {
  locations: LocationDTO[],
  entity: Entity,
  selectedLocation: LocationDTO,
  pendingSelectedLocation: LocationDTO,
  isFormDirty: boolean,
  isModalOpen: boolean,
};

class StructuresInformation extends React.Component<Props, State> {
  state = {
    locations: [],
    entity: null,
    selectedLocation: null,
    isFormDirty: false,
    isModalOpen: false,
    pendingSelectedLocation: null,
  };

  componentDidMount(): void {
    this.updateEntity();
    compteApi.fetchLocations({ value: this.props.entityId })
      .then((locations) => (
        this.setState({
          locations: locations.locations,
          selectedLocation: locations.locations.filter((l) =>
            l.locationType === LocationDTO.LocationTypeEnum.MAIN)[0],
        })))
      .catch(() => toastError(I18n.t('message.ERROR')));
    this.props.setModalI18n('HOME.CONFIRM_MODAL_TITLE', 'PROFESSIONAL_INFORMATION.CONFIRM_CLOSE');
  }

  updateEntity = (): void => {
    compteApi.fetchMyUserDetails()
      .then((myUserDetails) => {
        const entity = myUserDetails.entities
          .find((entityDTO) => entityDTO.id === this.props.entityId);
        this.setState(() => ({
          entity,
        }));
      });
  };

  getTypeEntityLabel = () => {
    let typeEntityLabel = 'la structure';
    if (this.state.entity?.typeEntite === TypeEntityEnum.OFFICE
      || this.state.entity?.typeEntite === TypeEntityEnum.INSTANCE
      || this.state.entity?.typeEntite === TypeEntityEnum.ORGANISME) {
      typeEntityLabel = `de l'${this.state.entity?.typeEntite}`;
    }
    if (this.state.entity?.typeEntite === TypeEntityEnum.PARTENAIRE
      || this.state.entity?.typeEntite === TypeEntityEnum.STON) {
      typeEntityLabel = `du ${this.state.entity?.typeEntite}`;
    }
    return typeEntityLabel;
  };

  getButtonLabel = (location: LocationDTO) => {
    if (location.locationType === LocationDTO.LocationTypeEnum.MAIN) {
      return <Translate value="BUREAU.PRINCIPAL" />;
    }

    if (location.commune) {
      return (
        <Translate
          value="BUREAU.ANNEXES"
          ville={location.commune}
        />
      );
    }
    return (
      <Translate
        value="BUREAU.ANNEXES_FALLBACK"
        number={location.ou.split('_')[1]}
      />
    );
  };

  setDirty = (dirty: boolean, callback: () => void) => {
    this.setState({ isFormDirty: dirty });
    this.props.setDirty(dirty, callback);
  };

  onClose = () => {
    this.setState((prevState: State) => ({
      selectedLocation: prevState.pendingSelectedLocation,
      pendingSelectedLocation: null,
      isModalOpen: false,
    }));
  };

  selectBureau = (location: LocationDTO) => {
    if (this.state.isFormDirty) {
      this.setState({ pendingSelectedLocation: location });
      this.toggleModal();
    } else this.setState({ selectedLocation: location });
  };

  toggleModal = () => {
    this.setState((prevState) => ({ isModalOpen: !prevState.isModalOpen }));
  };

  render() {
    let isManager;
    if (this.props.location.state) {
      isManager = this.props.location.state.isManager;
    } else this.props.history.push(HOME_PAGE_ROUTE);
    return (
      <div className="structure-drawer">
        <ConfirmationModal
          isOpen={this.state.isModalOpen}
          onClose={this.toggleModal}
          cancelButtonI18nKey="HOME.CONFIRM_MODAL_CANCEL"
          confirmationButtonI18nKey="STRUCTURE_INFORMATION.CONFIRM_CHANGE"
          titleI18nKey="HOME.CONFIRM_MODAL_TITLE"
          subtTitleI18nKey="STRUCTURE_INFORMATION.CONFIRM_BUREAU_CHANGE"
          onConfirm={() => {
            this.setDirty(false, this.onClose);
          }}
          confirmeDisabled={false}
          small
        />
        <div className="structure-drawer-header">
          {this.props.backButton || ''}
          <div className="drawer-title">
            <h3>
              {getStructureName(this.state.entity)
              || (<Translate value="STRUCTURE_INFORMATION.NAME_PLACEHOLDER" />)}
            </h3>
            <div className="subtitle">
              <Translate value="STRUCTURE_INFORMATION.STRUCTURE_INFORMATION" typeEntity={this.getTypeEntityLabel()} />
            </div>
            <div className="additional-subtitle">
              <Translate value="PROFESSIONAL_INFORMATION.PUBLIC_INFORMATION" />
            </div>
          </div>
          {
            this.state.locations
            && this.state.locations.length === 1
            && <div className="drawer-divider-horizontal" />
          }
        </div>
        {
          this.state.locations
          && this.state.locations.length > 1
          && (
            <div className="select-bureau">
              <div className="onglets">
                {this.state.locations.map((location: LocationDTO) => (
                  <button
                    type="button"
                    key={location.ou}
                    className={`onglet-bureau ${this.state.selectedLocation.ou === location.ou ? 'selected' : ''}`}
                    onClick={() => this.selectBureau(location)}
                  >
                    {this.getButtonLabel(location)}
                  </button>
                ))}
              </div>
              <div className="drawer-divider-horizontal" />
            </div>
          )
        }
        {this.state.entity
        && this.state.locations
          ? (
            <StructureInformation
              entity={this.state.entity}
              isManager={isManager}
              location={this.state.selectedLocation}
              onClose={this.props.onClose}
              setDirty={this.setDirty}
            />
          )
          : (
            <div className="loader-container">
              <Loader />
            </div>
          )}
      </div>
    );
  }
}

export default withRouter(StructuresInformation);
